const EMPTY_VALUE = '-';

function isPresent(value) {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'string') {
    return value.trim() !== '';
  }

  return true;
}

function getRawValue(source, keys) {
  if (!source || typeof source !== 'object') {
    return null;
  }

  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(source, key)) {
      continue;
    }

    const value = source[key];

    if (!isPresent(value)) {
      continue;
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
      const name = value.name;

      if (!isPresent(name)) {
        continue;
      }

      return {
        type: 'text',
        value: String(name).trim(),
        id: value.id !== null && value.id !== undefined ? String(value.id) : null
      };
    }

    return {
      type: 'code',
      value: String(value).trim(),
      id: null
    };
  }

  return null;
}

function getStringValue(source, keys) {
  if (!source || typeof source !== 'object') {
    return null;
  }

  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(source, key)) {
      continue;
    }

    const value = source[key];

    if (!isPresent(value) || typeof value === 'object') {
      continue;
    }

    const normalized = String(value).trim();

    if (normalized !== '') {
      return normalized;
    }
  }

  return null;
}

function translateCode(translate, dictionaryPath, code) {
  if (!code) {
    return null;
  }

  const key = `${dictionaryPath}${code}`;
  const label = translate(key);

  if (!label || label === key) {
    return null;
  }

  return label;
}

function formatCodedOrText(field, translate, dictionaryPath) {
  if (!field) {
    return EMPTY_VALUE;
  }

  if (field.type === 'text') {
    return field.value || EMPTY_VALUE;
  }

  return translateCode(translate, dictionaryPath, field.value) || field.value || EMPTY_VALUE;
}

function isOtherJobPosition(field) {
  if (!field) {
    return false;
  }

  if (field.type === 'code') {
    return field.value === '8';
  }

  return field.id === '8';
}

export function getWorkInfoView(workInfo, translate) {
  if (!workInfo || typeof workInfo !== 'object') {
    return {
      hasData: false,
      fields: []
    };
  }

  const employmentType = getRawValue(workInfo, ['employment_type']);
  const scopeOfEmployment = getRawValue(workInfo, ['scope_of_employment_type', 'employment_area']);
  const organizationName = getStringValue(workInfo, ['organization_name', 'company_name']);
  const workExperience = getStringValue(workInfo, ['workExperience', 'experience']);
  const organizationUnp = getStringValue(workInfo, ['organization_unp']);
  const jobPosition = getRawValue(workInfo, ['job_position_type', 'position']);
  const jobPositionName = getStringValue(workInfo, ['job_position_name']);
  const isOtherPosition = isOtherJobPosition(jobPosition);

  const fields = [
    {
      key: 'employmentType',
      labelKey: 'message.changeWorkInfoPage.fields.employmentType',
      value: formatCodedOrText(
        employmentType,
        translate,
        'message.signUp.jobBlock.typeOfEmployment.list.'
      )
    },
    {
      key: 'scopeOfEmployment',
      labelKey: 'message.changeWorkInfoPage.fields.scopeOfEmployment',
      value: formatCodedOrText(
        scopeOfEmployment,
        translate,
        'message.signUp.jobBlock.fieldOfEmployment.list.'
      )
    },
    {
      key: 'organizationName',
      labelKey: 'message.changeWorkInfoPage.fields.organizationName',
      value: organizationName || EMPTY_VALUE
    }
  ];

  if (isOtherPosition) {
    fields.push({
      key: 'jobPositionName',
      labelKey: 'message.changeWorkInfoPage.fields.jobPositionName',
      value: jobPositionName || (jobPosition?.type === 'text' ? jobPosition.value : null) || EMPTY_VALUE
    });
  } else {
    fields.push({
      key: 'jobPosition',
      labelKey: 'message.changeWorkInfoPage.fields.jobPosition',
      value: formatCodedOrText(
        jobPosition,
        translate,
        'message.signUp.jobBlock.post.list.'
      )
    });
  }

  fields.push({
    key: 'workExperience',
    labelKey: 'message.changeWorkInfoPage.fields.workExperience',
    value: workExperience || EMPTY_VALUE
  });

  if (organizationUnp) {
    fields.push({
      key: 'organizationUnp',
      labelKey: 'message.changeWorkInfoPage.fields.organizationUnp',
      value: organizationUnp
    });
  }

  return {
    hasData: true,
    fields
  };
}

const WORK_INFO_EMPLOYMENT_TYPE_OPTIONS = ['1', '11', '2', '3', '4', '5', '6', '7', '8', '9', '10'];
const WORK_INFO_SCOPE_OPTIONS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16'];
const WORK_INFO_JOB_POSITION_OPTIONS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];
const WORK_INFO_SCOPE_SELECTABLE_OPTIONS = WORK_INFO_SCOPE_OPTIONS.filter((option) => option !== '16');
const WORK_INFO_JOB_POSITION_SELECTABLE_OPTIONS = WORK_INFO_JOB_POSITION_OPTIONS.filter((option) => option !== '9');
const DISABLED_EMPLOYMENT_TYPES = ['3', '4', '7', '10'];

function getNamedFieldValue(field) {
  if (!field || typeof field !== 'object') {
    return null;
  }

  if (field.name === null || field.name === undefined || field.name === '') {
    return null;
  }

  return String(field.name).trim();
}

function isLegacyWorkInfoStructure(workInfo) {
  if (!workInfo || typeof workInfo !== 'object') {
    return false;
  }

  return Boolean(
    workInfo.employment_area
    || (workInfo.employment_type && typeof workInfo.employment_type === 'object')
    || (workInfo.position && typeof workInfo.position === 'object')
  );
}

function toFormString(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  return String(value);
}

function getDictionaryOption(value, options) {
  const normalizedValue = toFormString(value);

  if (!normalizedValue || !options.includes(normalizedValue)) {
    return '';
  }

  return normalizedValue;
}

export function isCustomJobPositionType(jobPositionType) {
  return jobPositionType === '8' || jobPositionType === 8;
}

export function isEmploymentTypeWithDisabledWorkFields(employmentType) {
  return DISABLED_EMPLOYMENT_TYPES.includes(toFormString(employmentType));
}

export function getDisabledEmploymentWorkFields() {
  return {
    scope_of_employment_type: '16',
    organization_name: '-',
    job_position_type: '9',
    job_position_name: '',
    workExperience: '0'
  };
}

export function getDefaultEmploymentWorkFields() {
  return {
    scope_of_employment_type: '1',
    organization_name: '',
    job_position_type: '1',
    job_position_name: ''
  };
}

export function getWorkInfoEmploymentTypeOptions() {
  return [...WORK_INFO_EMPLOYMENT_TYPE_OPTIONS];
}

export function getWorkInfoScopeOfEmploymentOptions() {
  return [...WORK_INFO_SCOPE_SELECTABLE_OPTIONS];
}

export function getWorkInfoJobPositionOptions() {
  return [...WORK_INFO_JOB_POSITION_SELECTABLE_OPTIONS];
}

export function buildWorkInfoEditForm(workInfo) {
  const emptyForm = {
    employment_type: '',
    scope_of_employment_type: '',
    organization_name: '',
    job_position_type: '',
    job_position_name: '',
    workExperience: '',
    unresolved: {
      employment_type: null,
      scope_of_employment_type: null,
      job_position_type: null
    }
  };

  if (!workInfo || typeof workInfo !== 'object') {
    return emptyForm;
  }

  if (isLegacyWorkInfoStructure(workInfo)) {
    return {
      employment_type: '',
      scope_of_employment_type: '',
      organization_name: toFormString(workInfo.company_name ?? workInfo.organization_name),
      job_position_type: '',
      job_position_name: '',
      workExperience: toFormString(workInfo.experience ?? workInfo.workExperience),
      unresolved: {
        employment_type: getNamedFieldValue(workInfo.employment_type),
        scope_of_employment_type: getNamedFieldValue(workInfo.employment_area),
        job_position_type: getNamedFieldValue(workInfo.position)
      }
    };
  }

  const jobPositionType = getDictionaryOption(
    workInfo.job_position_type,
    WORK_INFO_JOB_POSITION_OPTIONS
  );

  return {
    employment_type: getDictionaryOption(workInfo.employment_type, WORK_INFO_EMPLOYMENT_TYPE_OPTIONS),
    scope_of_employment_type: getDictionaryOption(
      workInfo.scope_of_employment_type,
      WORK_INFO_SCOPE_OPTIONS
    ),
    organization_name: toFormString(workInfo.organization_name ?? workInfo.company_name),
    job_position_type: jobPositionType,
    job_position_name: isCustomJobPositionType(jobPositionType)
      ? toFormString(workInfo.job_position_name)
      : '',
    workExperience: toFormString(workInfo.workExperience ?? workInfo.experience),
    unresolved: {
      employment_type: null,
      scope_of_employment_type: null,
      job_position_type: null
    }
  };
}
